import { areConnectionsEqual } from "@/data-preparation-tool/utils/edge-utils";
import { generateGUID, GUID } from "@faro-lotv/foundation";
import {
  CreateOrUpdateRegistrationEdgeParams,
  isDeletedRevisionStatus,
  RegistrationEdgeRevision,
  RegistrationEdgeType,
  RevisionStatus,
} from "@faro-lotv/service-wires";
import { EntityMap } from "../revision/revision-slice";
import { getAncestorIds } from "../revision/selector-helpers";
import { ConnectionEdit, UserEditTransformScans } from "./user-edit-types";

export type UpdatedRevisionEdges = {
  /** The edges to create or update based on user edits. */
  newEdges: Array<
    Pick<
      RegistrationEdgeRevision,
      keyof Omit<CreateOrUpdateRegistrationEdgeParams, "data">
    >
  >;
  /** The IDs of the edges to delete based on user edits. */
  deletedEdges: Set<GUID>;
};

/** Edge types to delete when the user explicitly deletes a connection. */
export const EDGES_DELETED_BY_DELETED_CONNECTION = [
  RegistrationEdgeType.candidate,
  RegistrationEdgeType.global,
  RegistrationEdgeType.local,
];

/** Edge types to delete (implicitly) when the user edits the transform of the connected scans differently. */
export const EDGES_DELETED_BY_SCAN_TRANSFORM = [
  RegistrationEdgeType.global,
  RegistrationEdgeType.local,
];

/**
 *
 * @param edge The registration edge to check
 * @param transformEdit The transform edit made by the user
 * @param entityMap The entities in the revision
 * @returns true if the edge is invalidated and should be deleted by the transform edit
 */
export function isEdgeInvalidatedByTransformEdit(
  edge: RegistrationEdgeRevision,
  transformEdit: UserEditTransformScans,
  entityMap: EntityMap,
): boolean {
  const sourceAncestors = getAncestorIds(entityMap, edge.sourceId);
  const targetAncestors = getAncestorIds(entityMap, edge.targetId);

  const sourceEdited = sourceAncestors.some((id) =>
    transformEdit.scanIds.includes(id),
  );
  const targetEdited = targetAncestors.some((id) =>
    transformEdit.scanIds.includes(id),
  );

  return sourceEdited !== targetEdited;
}

/**
 * @param allEdges The existing edges in the revision
 * @param edgeType The type of edge to create or update
 * @param edit The edit information for the edge
 * @returns The created or updated edge, or undefined if no changes were made
 */
export function createEdgeEditIfNecessary(
  allEdges: RegistrationEdgeRevision[],
  edgeType: RegistrationEdgeType,
  edit: ConnectionEdit,
): UpdatedRevisionEdges["newEdges"][number] | undefined {
  const existingEdge = allEdges.find(
    (edge) => edge.type === edgeType && areConnectionsEqual(edge, edit),
  );
  if (existingEdge && !isDeletedRevisionStatus(existingEdge.status)) {
    // The edge already exists, don't duplicate it
    return;
  }

  // If an edge already exists but is deleted, it needs to be re-used to prevent API errors
  return {
    id: existingEdge ? existingEdge.id : generateGUID(),
    status: existingEdge ? RevisionStatus.modified : RevisionStatus.added,
    sourceId: edit.sourceId,
    targetId: edit.targetId,
    type: edgeType,
    createdBy: existingEdge ? existingEdge.createdBy : edit.createdBy,
    createdAt: existingEdge ? existingEdge.createdAt : edit.createdAt,
    lastPatchedBy: edit.createdBy,
    lastPatchedAt: edit.createdAt,
  };
}
